import React from 'react';
import styled from 'styled-components';
import Card from '../../Card';
import GlassPicklist, { type GlassPicklistOption } from '../../ui/GlassPicklist';
import {
  DAY_IN_MS,
  roundDurationToWholeDaysMs,
  toLocalDayEndIso,
  toLocalDayEndMs,
  toLocalDayStartIso,
  toLocalDayStartMs,
} from '../../calendar/calendarUtils';

type TimelineRange = 'week' | 'two_weeks' | 'month' | 'quarter' | 'year';
type DragMode = 'move' | 'resize-start' | 'resize-end';

type LaneDateRange = {
  startAt: string;
  endAt: string;
};

export type TeamTimelineLane = {
  id: string;
  label: string;
  subtitle?: string;
  status: string;
  startAt: string;
  endAt: string;
  updatedAt: string;
  openCount?: number;
  totalCount?: number;
};

type Props = {
  lanes: TeamTimelineLane[];
  scopeLabel: string;
  columnLabel?: string;
  height?: string;
  editable?: boolean;
  secondaryPicklist?: {
    value: string;
    options: ReadonlyArray<GlassPicklistOption<string>>;
    onChange: (next: string) => void;
    ariaLabel?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    emptyLabel?: string;
  };
  onLaneDateChange?: (laneId: string, nextRange: LaneDateRange) => Promise<void> | void;
  onLaneOrderChange?: (nextOrderedIds: string[]) => void;
  onCreateAtDate?: (dateIso: string) => void;
};

type PlottedLane = TeamTimelineLane & {
  startMs: number;
  endMs: number;
  updatedMs: number;
  startCol: number;
  endCol: number;
  isInRange: boolean;
};

type DragState = {
  id: string;
  mode: DragMode;
  originX: number;
  originalStartMs: number;
  originalEndMs: number;
  spanMs: number;
};

type PanState = {
  originX: number;
  originShiftMs: number;
};

const LEFT_COL_WIDTH_PX = 220;
const LANE_ROW_HEIGHT_PX = 52;
const APPENDED_EMPTY_ROWS = 1;

const RANGE_CONFIG: Record<TimelineRange, { label: string; days: number; segments: number; pxPerSegment: number }> = {
  week: { label: 'Week', days: 7, segments: 7, pxPerSegment: 128 },
  two_weeks: { label: 'Two weeks', days: 14, segments: 14, pxPerSegment: 108 },
  month: { label: 'Month', days: 30, segments: 15, pxPerSegment: 96 },
  quarter: { label: 'Quarter', days: 90, segments: 13, pxPerSegment: 106 },
  year: { label: 'Year', days: 365, segments: 12, pxPerSegment: 112 },
};

const RANGE_OPTIONS: ReadonlyArray<GlassPicklistOption<TimelineRange>> = [
  { value: 'week', label: RANGE_CONFIG.week.label },
  { value: 'two_weeks', label: RANGE_CONFIG.two_weeks.label },
  { value: 'month', label: RANGE_CONFIG.month.label },
  { value: 'quarter', label: RANGE_CONFIG.quarter.label },
  { value: 'year', label: RANGE_CONFIG.year.label },
];

const RANGE_ORDER: ReadonlyArray<TimelineRange> = ['week', 'two_weeks', 'month', 'quarter', 'year'];

const Wrap = styled(Card)<{ $height?: string }>`
  padding: 14px;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 10px;
  min-height: 0;
  height: ${({ $height }) => $height || 'auto'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
`;

const HeaderLead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: .95rem;
  font-weight: 900;
  letter-spacing: .3px;
  text-transform: uppercase;
  color: #E8EAED;
`;

const HeaderRange = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
  white-space: nowrap;
`;

const Meta = styled.div`
  color: #9aa4b2;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
`;

const Controls = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 10px;
`;

const ScopeSummary = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid rgba(224, 161, 94, .2);
  background: rgba(224, 161, 94, .05);
  padding: 7px 10px;
  display: grid;
  align-content: center;
  gap: 2px;
`;

const RangeControl = styled.div`
  width: 148px;
  max-width: 38vw;
  min-height: 44px;
  align-self: stretch;

  & > div {
    height: 100%;
  }

  & > div > button {
    height: 100%;
    min-height: 100%;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .14) !important;
    background: rgba(255, 255, 255, .04);
    padding: 0 30px;
    font-size: 12px;
    font-weight: 800;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > div > button:hover {
    border-color: rgba(224, 161, 94, .55) !important;
  }

  & > div > button:focus,
  & > div > button:focus-visible {
    border-color: rgba(224, 161, 94, .75) !important;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  & > div > span {
    right: 10px;
    font-size: .66rem;
  }
`;

const SecondaryControl = styled.div`
  width: 188px;
  max-width: 40vw;
  min-height: 44px;
  align-self: stretch;

  & > div {
    height: 100%;
  }

  & > div > button {
    height: 100%;
    min-height: 100%;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .14) !important;
    background: rgba(255, 255, 255, .04);
    padding: 0 30px;
    font-size: 12px;
    font-weight: 800;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > div > button:hover {
    border-color: rgba(224, 161, 94, .55) !important;
  }

  & > div > button:focus,
  & > div > button:focus-visible {
    border-color: rgba(224, 161, 94, .75) !important;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  & > div > span {
    right: 10px;
    font-size: .66rem;
  }
`;

const ScopeSummaryTitle = styled.div`
  color: #E8EAED;
  font-size: 12px;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScopeSummaryMeta = styled.div`
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CanvasScroller = styled.div<{ $grabbing: boolean }>`
  min-height: 0;
  border: 1px solid rgba(255, 255, 255, .08);
  border-radius: 12px;
  overflow: auto;
  overscroll-behavior: contain;
  cursor: ${({ $grabbing }) => ($grabbing ? 'grabbing' : 'grab')};

  /* Hide scrollbars while preserving scroll behavior */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const CanvasInner = styled.div<{ $timelineWidth: number }>`
  min-height: 100%;
  width: ${({ $timelineWidth }) => `${LEFT_COL_WIDTH_PX + $timelineWidth}px`};
  min-width: ${({ $timelineWidth }) => `${LEFT_COL_WIDTH_PX + $timelineWidth}px`};
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`;

const AxisRow = styled.div<{ $timelineWidth: number }>`
  display: grid;
  grid-template-columns: ${LEFT_COL_WIDTH_PX}px ${({ $timelineWidth }) => `${$timelineWidth}px`};
  min-height: 38px;
  border-bottom: 1px solid rgba(255, 255, 255, .07);
  background: rgba(255, 255, 255, .03);
`;

const AxisHead = styled.div`
  display: grid;
  align-content: center;
  padding: 8px 10px;
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
  border-right: 1px solid rgba(255, 255, 255, .08);
`;

const AxisTicks = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
`;

const AxisTick = styled.div`
  padding: 8px 4px;
  border-left: 1px solid rgba(255, 255, 255, .06);
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
  text-align: center;
`;

const Rows = styled.div`
  display: grid;
  align-content: start;
  min-height: 0;
`;

const LaneRow = styled.div<{ $active: boolean; $timelineWidth: number }>`
  cursor: pointer;
  display: grid;
  grid-template-columns: ${LEFT_COL_WIDTH_PX}px ${({ $timelineWidth }) => `${$timelineWidth}px`};
  min-height: 52px;
  border-bottom: 1px solid rgba(255, 255, 255, .05);
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .08)' : 'transparent')};

  &:hover {
    background: rgba(255, 255, 255, .04);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(224, 161, 94, .28);
  }
`;

const LaneInfo = styled.div<{
  $reorderable: boolean;
  $dragging: boolean;
  $dropBefore: boolean;
  $dropAfter: boolean;
}>`
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 2px;
  padding: 8px 10px;
  border-right: 1px solid rgba(255, 255, 255, .08);
  cursor: ${({ $reorderable, $dragging }) => ($reorderable ? ($dragging ? 'grabbing' : 'grab') : 'default')};
  opacity: ${({ $dragging }) => ($dragging ? 0.58 : 1)};
  box-shadow: ${({ $dropBefore, $dropAfter }) =>
    $dropBefore
      ? 'inset 0 2px 0 rgba(224, 161, 94, .82)'
      : ($dropAfter ? 'inset 0 -2px 0 rgba(224, 161, 94, .82)' : 'none')};
  background: ${({ $dragging }) => ($dragging ? 'rgba(224, 161, 94, .10)' : 'transparent')};
`;

const LaneTitle = styled.div`
  color: #E8EAED;
  font-size: 12px;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LaneSub = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Track = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  align-items: center;
  padding: 0 8px;
  min-height: 52px;
  background-image: linear-gradient(to right, rgba(255, 255, 255, .05) 1px, transparent 1px);
  background-size: calc(100% / ${({ $columns }) => $columns}) 100%;
  background-position: left top;
`;

const Bar = styled.div<{ $start: number; $end: number; $status: string; $active: boolean; $editable: boolean; $dragging: boolean }>`
  grid-column: ${({ $start, $end }) => `${$start} / ${$end}`};
  height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .2);
  background: ${({ $status }) => statusColor($status)};
  box-shadow: ${({ $active }) => ($active ? '0 0 0 2px rgba(224, 161, 94, .28)' : 'none')};
  position: relative;
  cursor: ${({ $editable, $dragging }) => ($editable ? ($dragging ? 'grabbing' : 'grab') : 'default')};
  user-select: none;
`;

const Handle = styled.button<{ $side: 'start' | 'end' }>`
  all: unset;
  position: absolute;
  top: -1px;
  bottom: -1px;
  width: 11px;
  cursor: ew-resize;
  ${({ $side }) => ($side === 'start' ? 'left: 0;' : 'right: 0;')}

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    bottom: 3px;
    width: 2px;
    background: rgba(255, 255, 255, .8);
    border-radius: 2px;
    ${({ $side }) => ($side === 'start' ? 'left: 4px;' : 'right: 4px;')}
  }
`;

const EmptyLaneRow = styled.div<{ $timelineWidth: number }>`
  display: grid;
  grid-template-columns: ${LEFT_COL_WIDTH_PX}px ${({ $timelineWidth }) => `${$timelineWidth}px`};
  min-height: 90px;
  border-bottom: 1px solid rgba(255, 255, 255, .05);
`;

const EmptyLaneInfo = styled.div`
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 2px;
  padding: 8px 10px;
  border-right: 1px solid rgba(255, 255, 255, .08);
`;

const EmptyLaneLabel = styled.div`
  color: #9aa4b2;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
`;

const EmptyHint = styled.div`
  grid-column: 1 / -1;
  color: #9aa4b2;
  font-size: 13px;
  font-weight: 700;
  padding: 0 12px;
`;

const OutOfRangeHint = styled.div`
  grid-column: 1 / -1;
  color: #7f8ba0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .18px;
  padding: 0 10px;
`;

const EmptyCreateRow = styled.div<{ $timelineWidth: number }>`
  display: grid;
  grid-template-columns: ${LEFT_COL_WIDTH_PX}px ${({ $timelineWidth }) => `${$timelineWidth}px`};
  min-height: ${LANE_ROW_HEIGHT_PX}px;
  border-bottom: 1px solid rgba(255, 255, 255, .05);
`;

const EmptyCreateInfo = styled.div`
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 2px;
  padding: 8px 10px;
  border-right: 1px solid rgba(255, 255, 255, .08);
`;

const EmptyCreateTitle = styled.div`
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyCreateSub = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyCreateTrack = styled.div<{ $columns: number; $clickable: boolean }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  align-items: center;
  padding: 0 8px;
  min-height: ${LANE_ROW_HEIGHT_PX}px;
  background-image: linear-gradient(to right, rgba(255, 255, 255, .05) 1px, transparent 1px);
  background-size: calc(100% / ${({ $columns }) => $columns}) 100%;
  background-position: left top;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${({ $clickable }) => ($clickable ? 'rgba(255, 255, 255, .03)' : 'transparent')};
  }
`;

function statusColor(status: string) {
  if (status === 'in_progress') return 'linear-gradient(90deg, #10B981, #34D399)';
  if (status === 'blocked') return 'linear-gradient(90deg, #F59E0B, #FBBF24)';
  if (status === 'resolved' || status === 'delivered') return 'linear-gradient(90deg, #3B82F6, #60A5FA)';
  if (status === 'closed') return 'linear-gradient(90deg, #6B7280, #9CA3AF)';
  if (status === 'submitted' || status === 'open' || status === 'triage') {
    return 'linear-gradient(90deg, #6366F1, #8B5CF6)';
  }
  return 'linear-gradient(90deg, #64748B, #94A3B8)';
}

function parseMs(value: string): number | null {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatDateLabel(isoOrMs: string | number, compact = false) {
  const date = new Date(isoOrMs);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, compact ? { month: 'short', day: 'numeric' } : {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTick(ms: number, range: TimelineRange) {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  if (range === 'year') {
    return date.toLocaleDateString(undefined, { month: 'short' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatStatus(status: string) {
  return status.replace(/_/g, ' ');
}

function getAdjacentRange(current: TimelineRange, direction: 'in' | 'out'): TimelineRange {
  const index = RANGE_ORDER.indexOf(current);
  if (index < 0) return current;
  const nextIndex =
    direction === 'in'
      ? Math.max(0, index - 1)
      : Math.min(RANGE_ORDER.length - 1, index + 1);
  return RANGE_ORDER[nextIndex];
}

export default function TeamTimelineCard({
  lanes,
  scopeLabel,
  columnLabel,
  height,
  editable = false,
  secondaryPicklist,
  onLaneDateChange,
  onLaneOrderChange,
  onCreateAtDate,
}: Props) {
  const [range, setRange] = React.useState<TimelineRange>('month');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const resolvedColumnLabel = columnLabel || scopeLabel;
  const [optimisticRanges, setOptimisticRanges] = React.useState<Record<string, LaneDateRange>>({});
  const [persistingId, setPersistingId] = React.useState<string | null>(null);
  const [dragState, setDragState] = React.useState<DragState | null>(null);
  const [panState, setPanState] = React.useState<PanState | null>(null);
  const [timelineShiftMs, setTimelineShiftMs] = React.useState(0);
  const [scrollerClientWidth, setScrollerClientWidth] = React.useState(0);
  const [draggingLaneId, setDraggingLaneId] = React.useState<string | null>(null);
  const [dropHint, setDropHint] = React.useState<{ laneId: string; position: 'before' | 'after' } | null>(null);
  const lastZoomAtRef = React.useRef(0);
  const panMovedRef = React.useRef(false);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);

  const optimisticRef = React.useRef(optimisticRanges);
  React.useEffect(() => {
    optimisticRef.current = optimisticRanges;
  }, [optimisticRanges]);

  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const sync = () => {
      setScrollerClientWidth(el.clientWidth);
    };

    sync();

    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const config = RANGE_CONFIG[range];
  const baseTimelineWidth = config.segments * config.pxPerSegment;
  const availableTimelineWidth = Math.max(0, scrollerClientWidth - LEFT_COL_WIDTH_PX);
  // Keep fixed cell count per range (week=7, two_weeks=14) but stretch cells
  // to fill the available canvas when there is extra horizontal space.
  const timelineWidth = Math.max(baseTimelineWidth, availableTimelineWidth);

  const anchoredRangeEnd = React.useMemo(() => {
    const nowMs = Date.now();
    const normalized = lanes.map((lane) => {
      const updatedSourceMs = parseMs(lane.updatedAt) ?? nowMs;
      const updatedMs = toLocalDayEndMs(updatedSourceMs);
      const rawEnd = parseMs(lane.endAt) ?? updatedSourceMs;
      const endMs = toLocalDayEndMs(Math.max(rawEnd, updatedSourceMs));
      return {
        endMs,
        updatedMs,
      };
    });

    return normalized.reduce(
      (max, lane) => Math.max(max, lane.endMs, lane.updatedMs),
      nowMs,
    );
  }, [lanes]);

  const baseRange = React.useMemo(() => {
    const spanMs = Math.max(config.days * DAY_IN_MS, DAY_IN_MS);
    const rangeEnd = toLocalDayEndMs(anchoredRangeEnd + timelineShiftMs);
    const rangeStart = rangeEnd - spanMs + 1;

    return {
      rangeStart,
      rangeEnd,
      spanMs,
    };
  }, [anchoredRangeEnd, config.days, timelineShiftMs]);

  const plotted = React.useMemo(() => {
    const nowMs = Date.now();
    const normalized: Array<Omit<PlottedLane, 'startCol' | 'endCol' | 'isInRange'>> = lanes.map((lane) => {
      const optimistic = optimisticRanges[lane.id];
      const updatedSourceMs = parseMs(lane.updatedAt) ?? nowMs;
      const updatedMs = toLocalDayEndMs(updatedSourceMs);
      const rawStart = parseMs(optimistic?.startAt ?? lane.startAt) ?? updatedSourceMs;
      const rawEnd = parseMs(optimistic?.endAt ?? lane.endAt) ?? updatedSourceMs;
      const startMs = toLocalDayStartMs(Math.min(rawStart, rawEnd));
      const endMs = Math.max(
        toLocalDayEndMs(Math.max(rawStart, rawEnd)),
        startMs + DAY_IN_MS - 1,
      );
      return {
        ...lane,
        startAt: optimistic?.startAt ?? lane.startAt,
        endAt: optimistic?.endAt ?? lane.endAt,
        startMs,
        endMs,
        updatedMs,
      };
    });

    const rangeStart = baseRange.rangeStart;
    const rangeEnd = baseRange.rangeEnd;
    const spanMs = baseRange.spanMs;

    const plottedLanes: PlottedLane[] = normalized
      .map((lane) => {
        const isInRange = lane.endMs >= rangeStart && lane.startMs <= rangeEnd;
        if (!isInRange) {
          // Keep ticket rows visible even when dates are outside the current window.
          // Do not render a timeline bar in this case.
          return { ...lane, startCol: 1, endCol: 2, isInRange: false };
        }

        const clampedStart = Math.max(lane.startMs, rangeStart);
        const clampedEnd = Math.min(lane.endMs, rangeEnd);
        const startRatio = (clampedStart - rangeStart) / spanMs;
        const endRatio = (clampedEnd - rangeStart + 1) / spanMs;
        const startCol = Math.min(
          config.segments,
          Math.max(1, Math.floor(startRatio * config.segments) + 1),
        );
        const endCol = Math.min(
          config.segments + 1,
          Math.max(startCol + 1, Math.ceil(endRatio * config.segments) + 1),
        );
        return { ...lane, startCol, endCol, isInRange: true };
      });

    const inRangeCount = plottedLanes.reduce(
      (count, lane) => (lane.isInRange ? count + 1 : count),
      0,
    );

    return {
      plottedLanes,
      rangeStart,
      rangeEnd,
      segments: config.segments,
      spanMs,
      inRangeCount,
    };
  }, [baseRange.rangeEnd, baseRange.rangeStart, baseRange.spanMs, config.segments, lanes, optimisticRanges]);

  const { plottedLanes, rangeStart, rangeEnd, segments, spanMs, inRangeCount } = plotted;

  const emptyCreateRowCount = React.useMemo(() => {
    if (!onCreateAtDate) return 0;
    return APPENDED_EMPTY_ROWS;
  }, [onCreateAtDate]);

  React.useEffect(() => {
    if (!plottedLanes.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !plottedLanes.some((lane) => lane.id === selectedId)) {
      setSelectedId(plottedLanes[0].id);
    }
  }, [plottedLanes, selectedId]);

  React.useEffect(() => {
    if (!draggingLaneId) return;
    if (plottedLanes.some((lane) => lane.id === draggingLaneId)) return;
    setDraggingLaneId(null);
    setDropHint(null);
  }, [draggingLaneId, plottedLanes]);

  const tickLabels = React.useMemo(() => {
    return Array.from({ length: segments }, (_, index) => {
      const ms = rangeStart + ((index / segments) * spanMs);
      return {
        key: `${index}-${ms}`,
        label: formatTick(ms, range),
      };
    });
  }, [rangeStart, range, segments, spanMs]);

  const selectedLane = React.useMemo(
    () => plottedLanes.find((lane) => lane.id === selectedId) || null,
    [plottedLanes, selectedId],
  );

  const laneOrderEditable = Boolean(onLaneOrderChange) && scopeLabel.toLowerCase() === 'tickets';

  const clearLaneReorderState = React.useCallback(() => {
    setDraggingLaneId(null);
    setDropHint(null);
  }, []);

  const reorderLanes = React.useCallback((
    draggingId: string,
    targetId: string,
    position: 'before' | 'after',
  ) => {
    if (!onLaneOrderChange) return;
    if (draggingId === targetId) return;

    const currentIds = plottedLanes.map((lane) => lane.id);
    const fromIndex = currentIds.indexOf(draggingId);
    const targetIndex = currentIds.indexOf(targetId);
    if (fromIndex < 0 || targetIndex < 0) return;

    const nextIds = [...currentIds];
    nextIds.splice(fromIndex, 1);
    const resolvedTargetIndex = nextIds.indexOf(targetId);
    if (resolvedTargetIndex < 0) return;
    const insertIndex = position === 'after' ? resolvedTargetIndex + 1 : resolvedTargetIndex;
    nextIds.splice(insertIndex, 0, draggingId);
    onLaneOrderChange(nextIds);
  }, [onLaneOrderChange, plottedLanes]);

  const resolveDropPosition = React.useCallback((event: React.DragEvent<HTMLDivElement>): 'before' | 'after' => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY < (rect.top + rect.height / 2) ? 'before' : 'after';
  }, []);

  const handleLaneInfoDragStart = React.useCallback((
    event: React.DragEvent<HTMLDivElement>,
    laneId: string,
  ) => {
    if (!laneOrderEditable || dragState) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', laneId);
    setDraggingLaneId(laneId);
    setDropHint(null);
  }, [dragState, laneOrderEditable]);

  const handleLaneInfoDragOver = React.useCallback((
    event: React.DragEvent<HTMLDivElement>,
    laneId: string,
  ) => {
    if (!laneOrderEditable) return;
    if (!draggingLaneId || draggingLaneId === laneId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const position = resolveDropPosition(event);
    setDropHint((previous) => {
      if (previous?.laneId === laneId && previous.position === position) return previous;
      return { laneId, position };
    });
  }, [draggingLaneId, laneOrderEditable, resolveDropPosition]);

  const handleLaneInfoDrop = React.useCallback((
    event: React.DragEvent<HTMLDivElement>,
    laneId: string,
  ) => {
    if (!laneOrderEditable) return;
    if (!draggingLaneId) return;
    event.preventDefault();
    event.stopPropagation();
    const position = resolveDropPosition(event);
    reorderLanes(draggingLaneId, laneId, position);
    clearLaneReorderState();
  }, [clearLaneReorderState, draggingLaneId, laneOrderEditable, reorderLanes, resolveDropPosition]);

  const handleLaneInfoDragEnd = React.useCallback(() => {
    clearLaneReorderState();
  }, [clearLaneReorderState]);

  const startDrag = React.useCallback(
    (event: React.MouseEvent, lane: PlottedLane, mode: DragMode) => {
      if (!editable) return;
      event.preventDefault();
      event.stopPropagation();

      setSelectedId(lane.id);
      setDragState({
        id: lane.id,
        mode,
        originX: event.clientX,
        originalStartMs: lane.startMs,
        originalEndMs: lane.endMs,
        spanMs,
      });
    },
    [editable, spanMs],
  );

  React.useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaPx = event.clientX - dragState.originX;
      const deltaMs = roundDurationToWholeDaysMs((deltaPx / Math.max(timelineWidth, 1)) * dragState.spanMs);

      let nextStartMs = dragState.originalStartMs;
      let nextEndMs = dragState.originalEndMs;

      if (dragState.mode === 'move') {
        nextStartMs += deltaMs;
        nextEndMs += deltaMs;
      } else if (dragState.mode === 'resize-start') {
        nextStartMs = Math.min(
          dragState.originalEndMs - DAY_IN_MS + 1,
          dragState.originalStartMs + deltaMs,
        );
      } else {
        nextEndMs = Math.max(
          dragState.originalStartMs + DAY_IN_MS - 1,
          dragState.originalEndMs + deltaMs,
        );
      }

      setOptimisticRanges((prev) => ({
        ...prev,
        [dragState.id]: {
          startAt: toLocalDayStartIso(nextStartMs),
          endAt: toLocalDayEndIso(nextEndMs),
        },
      }));
    };

    const handleMouseUp = () => {
      const finalize = async () => {
        const latest = optimisticRef.current[dragState.id];
        const originalStartIso = toLocalDayStartIso(dragState.originalStartMs);
        const originalEndIso = toLocalDayEndIso(dragState.originalEndMs);

        setDragState(null);

        if (!latest) return;

        const changed = latest.startAt !== originalStartIso || latest.endAt !== originalEndIso;
        if (!changed) {
          setOptimisticRanges((prev) => {
            const next = { ...prev };
            delete next[dragState.id];
            return next;
          });
          return;
        }

        if (!onLaneDateChange) return;

        try {
          setPersistingId(dragState.id);
          await onLaneDateChange(dragState.id, latest);
          setOptimisticRanges((prev) => {
            const next = { ...prev };
            delete next[dragState.id];
            return next;
          });
        } catch (error) {
          console.error('[TeamTimelineCard] Failed to persist timeline lane date change', error);
          setOptimisticRanges((prev) => {
            const next = { ...prev };
            delete next[dragState.id];
            return next;
          });
        } finally {
          setPersistingId(null);
        }
      };

      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      void finalize();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, onLaneDateChange, timelineWidth]);

  const startTimelinePan = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (dragState) return;
    if (event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-pan-ignore="true"]')) return;

    event.preventDefault();
    panMovedRef.current = false;
    setPanState({
      originX: event.clientX,
      originShiftMs: timelineShiftMs,
    });
  }, [dragState, timelineShiftMs]);

  React.useEffect(() => {
    if (!panState) return;

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const handleMouseMove = (event: MouseEvent) => {
      if ((event.buttons & 1) === 0) {
        setPanState(null);
        return;
      }
      const deltaX = event.clientX - panState.originX;
      if (Math.abs(deltaX) > 3) panMovedRef.current = true;
      const deltaMs = roundDurationToWholeDaysMs((deltaX / Math.max(timelineWidth, 1)) * spanMs);
      setTimelineShiftMs(panState.originShiftMs - deltaMs);
    };

    const handleMouseUp = () => {
      setPanState(null);
    };

    const handleWindowBlur = () => {
      setPanState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [panState, spanMs, timelineWidth]);

  const suppressClickAfterPan = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!panMovedRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    panMovedRef.current = false;
  }, []);

  const resolveSeedDateIsoForPointer = React.useCallback((pointerX: number, trackWidth: number): string | null => {
    if (trackWidth <= 0) return null;
    if (segments <= 0) return null;

    const clampedPointerX = Math.max(0, Math.min(trackWidth - 1, pointerX));
    const rawIndex = Math.floor((clampedPointerX / trackWidth) * segments);
    const segmentIndex = Math.max(0, Math.min(segments - 1, rawIndex));
    const segmentStartMs = rangeStart + ((segmentIndex / segments) * spanMs);
    return toLocalDayStartIso(segmentStartMs);
  }, [rangeStart, segments, spanMs]);

  const createAtPointer = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateAtDate) return;
    if (dragState) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-pan-ignore="true"]')) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - rect.left - LEFT_COL_WIDTH_PX + event.currentTarget.scrollLeft;
    if (pointerX < 0 || pointerX > timelineWidth) return;
    const seedDateIso = resolveSeedDateIsoForPointer(pointerX, timelineWidth);
    if (!seedDateIso) return;
    onCreateAtDate(seedDateIso);
  }, [dragState, onCreateAtDate, resolveSeedDateIsoForPointer, timelineWidth]);

  const createAtTrackPointer = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateAtDate) return;
    if (dragState) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;

    const pointerX = event.clientX - rect.left;
    if (pointerX < 0 || pointerX > rect.width) return;
    const seedDateIso = resolveSeedDateIsoForPointer(pointerX, rect.width);
    if (!seedDateIso) return;
    onCreateAtDate(seedDateIso);
  }, [dragState, onCreateAtDate, resolveSeedDateIsoForPointer]);

  const handleTimelineWheel = React.useCallback((event: WheelEvent) => {
    if (dragState) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const target = event.target;
    const overTicketColumn = target instanceof Element
      && Boolean(target.closest('[data-ticket-column="true"]'));
    const previousScrollTop = scroller.scrollTop;

    // Always cancel native wheel scroll so behavior is fully controlled.
    event.preventDefault();

    // Ticket column owns vertical wheel scrolling.
    if (overTicketColumn) {
      if (event.deltaY !== 0) {
        scroller.scrollTop = previousScrollTop + event.deltaY;
      }
      return;
    }

    // Day-cells must never leak into vertical movement.
    if (scroller.scrollTop !== previousScrollTop) {
      scroller.scrollTop = previousScrollTop;
    }

    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);
    const rect = scroller.getBoundingClientRect();

    if (absX > absY + 2) {
      const deltaMs = roundDurationToWholeDaysMs((event.deltaX / Math.max(timelineWidth, 1)) * spanMs);
      setTimelineShiftMs((current) => current + deltaMs);
      return;
    }

    if (absY < 10) return;
    const now = Date.now();
    if (now - lastZoomAtRef.current < 120) return;
    lastZoomAtRef.current = now;

    const direction: 'in' | 'out' = event.deltaY < 0 ? 'in' : 'out';
    const nextRange = getAdjacentRange(range, direction);
    if (nextRange === range) return;

    const pointerX = event.clientX - rect.left - LEFT_COL_WIDTH_PX + scroller.scrollLeft;
    const ratio = Math.max(0, Math.min(1, pointerX / Math.max(timelineWidth, 1)));
    const anchorMs = rangeStart + (ratio * spanMs);
    const nextSpanMs = Math.max(RANGE_CONFIG[nextRange].days * DAY_IN_MS, DAY_IN_MS);
    const nextRangeEnd = anchorMs + ((1 - ratio) * nextSpanMs);
    const nextShiftMs = roundDurationToWholeDaysMs(nextRangeEnd - anchoredRangeEnd);

    setRange(nextRange);
    setTimelineShiftMs(nextShiftMs);
  }, [anchoredRangeEnd, dragState, range, rangeStart, spanMs, timelineWidth]);

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const handleNativeWheel = (event: WheelEvent) => {
      handleTimelineWheel(event);
    };

    scroller.addEventListener('wheel', handleNativeWheel, { passive: false, capture: true });
    return () => {
      scroller.removeEventListener('wheel', handleNativeWheel, true);
    };
  }, [handleTimelineWheel]);

  return (
    <Wrap $height={height}>
      <Header>
        <HeaderLead>
          <Title>Timeline</Title>
          <HeaderRange>
            {formatDateLabel(rangeStart, true)} - {formatDateLabel(rangeEnd, true)}
          </HeaderRange>
        </HeaderLead>
        <Meta>{inRangeCount} {scopeLabel.toLowerCase()} in range</Meta>
      </Header>

      <Controls>
        <ScopeSummary>
          <ScopeSummaryTitle>{selectedLane?.label || resolvedColumnLabel}</ScopeSummaryTitle>
          {selectedLane ? (
            <>
              <ScopeSummaryMeta>
                {formatStatus(selectedLane.status)} | {formatDateLabel(selectedLane.startAt)} - {formatDateLabel(selectedLane.endAt)}
              </ScopeSummaryMeta>
              {selectedLane.subtitle ? <ScopeSummaryMeta>{selectedLane.subtitle}</ScopeSummaryMeta> : null}
              {persistingId === selectedLane.id ? <ScopeSummaryMeta>Saving schedule...</ScopeSummaryMeta> : null}
            </>
          ) : (
            <ScopeSummaryMeta>{resolvedColumnLabel}</ScopeSummaryMeta>
          )}
        </ScopeSummary>
        <RangeControl>
          <GlassPicklist<TimelineRange>
            value={range}
            options={RANGE_OPTIONS}
            onChange={setRange}
            ariaLabel="Timeline range"
          />
        </RangeControl>
        {secondaryPicklist ? (
          <SecondaryControl>
            <GlassPicklist<string>
              value={secondaryPicklist.value}
              options={secondaryPicklist.options}
              onChange={secondaryPicklist.onChange}
              ariaLabel={secondaryPicklist.ariaLabel ?? 'Timeline secondary filter'}
              searchable={secondaryPicklist.searchable}
              searchPlaceholder={secondaryPicklist.searchPlaceholder}
              emptyLabel={secondaryPicklist.emptyLabel}
            />
          </SecondaryControl>
        ) : null}
      </Controls>

      <CanvasScroller
        ref={scrollerRef}
        $grabbing={Boolean(panState)}
        onMouseDown={startTimelinePan}
        onClickCapture={suppressClickAfterPan}
        onDoubleClick={onCreateAtDate ? createAtPointer : undefined}
      >
        <CanvasInner $timelineWidth={timelineWidth}>
          <AxisRow $timelineWidth={timelineWidth}>
            <AxisHead data-ticket-column="true">{resolvedColumnLabel}</AxisHead>
            <AxisTicks $columns={segments}>
              {tickLabels.map((tick) => (
                <AxisTick key={tick.key}>{tick.label}</AxisTick>
              ))}
            </AxisTicks>
          </AxisRow>

          <Rows>
            {plottedLanes.length || emptyCreateRowCount ? (
              <>
                {plottedLanes.map((lane) => {
                  const laneEditable = editable && scopeLabel.toLowerCase() === 'tickets';
                  const laneIsDragging = draggingLaneId === lane.id;
                  const laneDropBefore = dropHint?.laneId === lane.id && dropHint.position === 'before';
                  const laneDropAfter = dropHint?.laneId === lane.id && dropHint.position === 'after';
                  return (
                    <LaneRow
                      key={lane.id}
                      $active={selectedId === lane.id}
                      $timelineWidth={timelineWidth}
                      onClick={() => setSelectedId(lane.id)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        setSelectedId(lane.id);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <LaneInfo
                        data-ticket-column="true"
                        data-pan-ignore={laneOrderEditable ? 'true' : undefined}
                        $reorderable={laneOrderEditable}
                        $dragging={laneIsDragging}
                        $dropBefore={laneDropBefore}
                        $dropAfter={laneDropAfter}
                        draggable={laneOrderEditable && !dragState}
                        onDragStart={(event) => handleLaneInfoDragStart(event, lane.id)}
                        onDragOver={(event) => handleLaneInfoDragOver(event, lane.id)}
                        onDrop={(event) => handleLaneInfoDrop(event, lane.id)}
                        onDragEnd={handleLaneInfoDragEnd}
                      >
                        <LaneTitle>{lane.label}</LaneTitle>
                        <LaneSub>{lane.subtitle || `${lane.totalCount ?? 0} tickets`}</LaneSub>
                      </LaneInfo>
                      <Track $columns={segments}>
                        {lane.isInRange ? (
                          <Bar
                            $start={lane.startCol}
                            $end={lane.endCol}
                            $status={lane.status}
                            $active={selectedId === lane.id}
                            $editable={laneEditable}
                            $dragging={dragState?.id === lane.id && dragState.mode === 'move'}
                            title={`${lane.label} (${formatStatus(lane.status)})`}
                            data-pan-ignore={laneEditable ? 'true' : undefined}
                            onMouseDown={laneEditable ? (event) => startDrag(event, lane, 'move') : undefined}
                          >
                            {laneEditable ? (
                              <>
                                <Handle
                                  type="button"
                                  $side="start"
                                  aria-label="Resize start"
                                  data-pan-ignore="true"
                                  onMouseDown={(event) => startDrag(event, lane, 'resize-start')}
                                />
                                <Handle
                                  type="button"
                                  $side="end"
                                  aria-label="Resize end"
                                  data-pan-ignore="true"
                                  onMouseDown={(event) => startDrag(event, lane, 'resize-end')}
                                />
                              </>
                            ) : null}
                          </Bar>
                        ) : (
                          <OutOfRangeHint>Out of visible range</OutOfRangeHint>
                        )}
                      </Track>
                    </LaneRow>
                  );
                })}

                {Array.from({ length: emptyCreateRowCount }, (_, index) => (
                  <EmptyCreateRow key={`empty-ticket-row-${index}`} $timelineWidth={timelineWidth}>
                    <EmptyCreateInfo data-ticket-column="true">
                      <EmptyCreateTitle>Empty ticket</EmptyCreateTitle>
                      <EmptyCreateSub>
                        {index === 0 && onCreateAtDate
                          ? 'Click any date cell to create a ticket.'
                          : 'Available slot'}
                      </EmptyCreateSub>
                    </EmptyCreateInfo>
                    <EmptyCreateTrack
                      $columns={segments}
                      $clickable={Boolean(onCreateAtDate)}
                      data-pan-ignore={onCreateAtDate ? 'true' : undefined}
                      onClick={onCreateAtDate ? createAtTrackPointer : undefined}
                    />
                  </EmptyCreateRow>
                ))}
              </>
            ) : (
              <EmptyLaneRow $timelineWidth={timelineWidth}>
                <EmptyLaneInfo data-ticket-column="true">
                  <EmptyLaneLabel>{scopeLabel}</EmptyLaneLabel>
                </EmptyLaneInfo>
                <Track $columns={segments}>
                  <EmptyHint>
                    No tickets in this window yet.
                    {onCreateAtDate
                      ? ' Timeline is continuous: drag left or right (or use horizontal scroll) to move across dates, and double-click any date cell to create a ticket.'
                      : ' Timeline is continuous: drag left or right (or use horizontal scroll) to move across dates.'}
                  </EmptyHint>
                </Track>
              </EmptyLaneRow>
            )}
          </Rows>
        </CanvasInner>
      </CanvasScroller>
    </Wrap>
  );
}
